import { css } from "@emotion/react";
import { FC } from "react";
import { RowInfo } from "react-table";

import { Avatar, DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import { duration } from "helpers/date";
import {
  IFirstResponseTimePerEntity,
  IHandlingTimeByStatusPerEntity,
  ISolvedAndClosedStats,
  ITicketRatingsPerEntity,
  ITicketResolutionTimePerEntity,
} from "services/api/report/interfaces";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

type TeamCellProps = {
  value: string;
  row: RowInfo["row"];
  teamName: string;
  totalLabel: string;
};

type AgentNameProps = {
  value: string;
  row: RowInfo["row"];
  agentName: string;
  avatar: string | null;
  totalLabel: string;
};

type RatingCellProps = {
  value: number;
  valueToCompare?: number;
  original: ITicketRatingsPerEntity | IFirstResponseTimePerEntity;
  accessor: "good" | "neutral" | "bad";
};

type ValueCellProps = {
  value: number;
  valueToCompare?: number;
  original:
    | IHandlingTimeByStatusPerEntity
    | IFirstResponseTimePerEntity
    | ITicketResolutionTimePerEntity
    | ISolvedAndClosedStats;
  formatAsTime?: boolean;
};

function calculateDifference(value: number, valueToCompare: number): number {
  return value - valueToCompare;
}

export const TeamCell: FC<TeamCellProps> = ({ value, row, teamName, totalLabel }) => {
  return (
    <span css={nameColumnStyles}>
      {value === "total" ? (
        <>
          <span css={rowNumberStyles}>{row._viewIndex + 1}.</span>
          <strong css={textEllipsisStyles}>{totalLabel}</strong>
        </>
      ) : (
        <>
          <span css={rowNumberStyles}>{row._viewIndex + 1}.</span>
          <Avatar
            css={avatarStyles}
            text={teamName}
            type={value === "null" || teamName === undefined ? "image" : "text"}
            size="xxsmall"
            shape="rounded-square"
          />
          <span css={textEllipsisStyles}>
            {value === "null" ? "Unassigned" : teamName === undefined ? "N/A" : teamName}
          </span>
        </>
      )}
    </span>
  );
};

export const AgentCell: FC<AgentNameProps> = ({ value, row, agentName, avatar, totalLabel }) => (
  <span css={nameColumnStyles}>
    {value === "total" ? (
      <>
        <span css={rowNumberStyles}>{row._viewIndex + 1}.</span>
        <strong css={textEllipsisStyles}>{totalLabel}</strong>
      </>
    ) : (
      <>
        <span css={rowNumberStyles}>{row._viewIndex + 1}.</span>
        <Avatar
          css={avatarStyles}
          type={avatar || value === "null" || agentName === undefined ? "image" : "text"}
          src={avatar ? avatar : undefined}
          text={agentName}
          size="xxsmall"
        />
        <span css={textEllipsisStyles}>
          {value === "null" ? "Unassigned" : agentName === undefined ? "N/A" : agentName}
        </span>
      </>
    )}
  </span>
);

export const ValueCell: FC<ValueCellProps> = ({ value, valueToCompare = 0, original, formatAsTime = true }) => {
  const isPositive = value - valueToCompare > 0;
  return (
    <span css={valueCellStyles}>
      <span>{formatAsTime ? duration(value) : value}</span>
      {original?.id !== "total" && (
        <span css={timeCellDifferenceStyles}>
          {isPositive ? "+" : ""}
          {formatAsTime ? duration(value - valueToCompare) : value - valueToCompare}
        </span>
      )}
    </span>
  );
};

export const RatingCell: FC<RatingCellProps> = ({ value, valueToCompare = 0, original, accessor }) => {
  const comparedValue = calculateDifference(value, valueToCompare);
  const isPositive = comparedValue > 0;
  return (
    <span css={valueCellStyles}>
      <span>
        {value}% ({original[`${accessor}Count` as keyof typeof original]})
      </span>
      {original.id !== "total" && (
        <span css={timeCellDifferenceStyles}>
          {isPositive ? "+" : ""}
          {comparedValue}%
        </span>
      )}
    </span>
  );
};

const nameColumnStyles = css`
  display: flex;
  align-items: center;
  width: 100%;
  height: 37px;
`;

const rowNumberStyles = css`
  flex: 0 0 20px;
  text-align: left;
  margin-right: ${withVar(SpacingToken.Spacing1)};
`;

const avatarStyles = css`
  flex: 0 0 auto;
  margin-right: ${withVar(SpacingToken.Spacing2)};
`;

const valueCellStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const timeCellDifferenceStyles = css`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
`;
